import { useState, useRef, useEffect, useCallback } from 'react';
import { AnaliseCadastral, SerasaExperianData, Negociacao } from '../../../../types';
import { supabase } from '../../../../src/lib/supabase';
import { useToast } from '../../../../src/contexts/ToastContext';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../../src/lib/logger';
import { Loader2, X, CreditCard, Percent, Zap, Clock, CheckCircle2, Info, Database, Building2, Users, User, FileWarning, Eye, Calendar as CalendarIcon, Upload, FileText, Trash2, ExternalLink } from 'lucide-react';

/** Format CPF/CNPJ for display */
const formatDocumento = (doc: string): string => {
    const clean = doc.replace(/\D/g, '');
    if (clean.length === 11) return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (clean.length === 14) return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return doc;
};

/** Time ago helper — linguagem leiga */
const timeAgo = (dateStr: string): string => {
    const now = new Date();
    const then = new Date(dateStr);
    const diffMs = now.getTime() - then.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'agora mesmo';
    if (diffMin < 60) return `há ${diffMin} minuto${diffMin > 1 ? 's' : ''}`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `há ${diffH} hora${diffH > 1 ? 's' : ''}`;
    const diffD = Math.floor(diffH / 24);
    return `há ${diffD} dia${diffD > 1 ? 's' : ''}`;
};

/** Format currency */
const fmtBRL = (v: number | undefined) => v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00';

interface Step4Props {
    data: Partial<AnaliseCadastral>;
    onChange: (data: Partial<AnaliseCadastral>) => void;
    negociacaoId: number;
    negociacao?: Negociacao | null;
}

export default function Step4Credito({ data, onChange, negociacaoId, negociacao }: Step4Props) {
    const { addToast } = useToast();
    const { user } = useAuth();
    const [gaugeAnimated, setGaugeAnimated] = useState(false);

    // Anexo do relatório Serasa (PDF/imagem)
    const [uploadingSerasa, setUploadingSerasa] = useState(false);
    const [draggingHeader, setDraggingHeader] = useState(false);
    const serasaInputRef = useRef<HTMLInputElement>(null);

    // Consulta automática ao Serasa REMOVIDA em 28/08/2026 (decisão do produto: a API
    // não é mais usada no VigorHub). O que sobrou aqui é leitura: os dados já
    // gravados em analise_cadastral.serasa_experian_data continuam sendo exibidos, e o
    // relatório de crédito passa a entrar exclusivamente pelo anexo (PDF/ZIP).
    // consultaTime/consultaUserName seguem alimentando o selo "consultado por X":
    // vêm de serasa_consultas_cache (histórico no banco), não da API.
    const [consultaTime, setConsultaTime] = useState<string | null>(null);
    const [consultaUserName, setConsultaUserName] = useState<string | null>(null);

    // Local string state for chance_pagamento
    const [chancePagamentoText, setChancePagamentoText] = useState(() => {
        const val = (data.serasa_experian_data as any)?.chance_pagamento;
        return val != null ? String(val).replace('.', ',') : '';
    });

    const serasa: SerasaExperianData = data.serasa_experian_data || {};
    const isPJ = negociacao?.tipo_cliente === 'PJ';

    // Check if data already exists (loaded from DB or previous consultation)
    const hasExistingData = !!(serasa.score || serasa.dividas_comerciais != null || serasa.situacao_receita_federal);

    useEffect(() => {
        const timer = setTimeout(() => setGaugeAnimated(true), 100);
        return () => clearTimeout(timer);
    }, []);

    // Auto-load persisted data from analise_cadastral on mount
    useEffect(() => {
        if (hasExistingData) {
            // Só busca quem consultou/quando para o selo — os dados em si já vieram
            // de analise_cadastral.
            fetchLastConsultaInfo();
        }
    }, []);

    const fetchLastConsultaInfo = useCallback(async () => {
        if (!negociacao?.cpf_cnpj) return;
        try {
            const cleanDoc = negociacao.cpf_cnpj.replace(/\D/g, '');
            const { data: lastConsulta } = await supabase
                .from('serasa_consultas_cache')
                .select('consultado_por, created_at')
                .eq('documento', cleanDoc)
                .order('created_at', { ascending: false })
                .limit(1)
                .single();

            if (lastConsulta) {
                setConsultaTime(lastConsulta.created_at);
                if (lastConsulta.consultado_por) {
                    const { data: profile } = await supabase
                        .from('profiles')
                        .select('full_name')
                        .eq('id', lastConsulta.consultado_por)
                        .single();
                    setConsultaUserName(profile?.full_name || 'Analista');
                }
            }
        } catch (err) {
            // silent
        }
    }, [negociacao?.cpf_cnpj]);

    useEffect(() => {
        const val = (data.serasa_experian_data as any)?.chance_pagamento;
        if (val != null) {
            const formatted = String(val).replace('.', ',');
            if (parseFloat(chancePagamentoText.replace(',', '.')) !== val) {
                setChancePagamentoText(formatted);
            }
        }
    }, [data.serasa_experian_data]);

    const updateSerasa = (field: keyof SerasaExperianData, value: any) => {
        onChange({ serasa_experian_data: { ...serasa, [field]: value } });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange({ [e.target.name]: e.target.value });
    };

    // ── Anexo do relatório Serasa (PDF/imagem/ZIP) ──
    // ZIP é necessário para análise coletiva: numa associação com 60 associados a
    // analista roda uma consulta SPC por CPF/CNPJ e anexa o pacote compactado, em
    // vez de 60 PDFs soltos (pedido da Dhaiane, 24/07/2026).
    const handleSerasaUpload = async (file: File) => {
        if (!/\.(pdf|png|jpe?g|zip)$/i.test(file.name)) {
            addToast('Anexe um PDF, imagem ou ZIP do relatório de crédito.', 'error');
            return;
        }
        setUploadingSerasa(true);
        try {
            const ext = file.name.split('.').pop();
            const path = `${negociacaoId}/serasa_${Date.now()}.${ext}`;
            const { error: uploadError } = await supabase.storage.from('analise-cadastral').upload(path, file, { upsert: true });
            if (uploadError) throw uploadError;
            const { data: urlData } = supabase.storage.from('analise-cadastral').getPublicUrl(path);
            onChange({ credito_relatorio_serasa_url: urlData.publicUrl });
            // Persiste imediatamente para não perder o anexo ao fechar o modal
            supabase
                .from('analise_cadastral')
                .update({ credito_relatorio_serasa_url: urlData.publicUrl })
                .eq('negociacao_id', negociacaoId)
                .then(({ error }) => {
                    if (error) console.warn('[Step4] Auto-save relatório Serasa falhou:', error.message);
                });
            addToast('Relatório Serasa anexado!', 'success');
            logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'anexar_relatorio_serasa', origem: 'acao' });
        } catch (error: any) {
            addToast(`Erro no upload: ${error.message}`, 'error');
        } finally {
            setUploadingSerasa(false);
        }
    };

    const handleRemoveSerasaFile = () => {
        onChange({ credito_relatorio_serasa_url: null as any });
        logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'remover_relatorio_serasa', origem: 'acao' });
        supabase
            .from('analise_cadastral')
            .update({ credito_relatorio_serasa_url: null })
            .eq('negociacao_id', negociacaoId)
            .then(({ error }) => {
                if (error) console.warn('[Step4] Remoção do relatório Serasa falhou:', error.message);
            });
    };

    // Escala oficial Serasa: 0–300 Alto Risco | 301–500 Risco Médio | 501–700 Baixo Risco | 701–1000 Muito Baixo Risco
    const getScoreColor = (score?: number) => {
        if (!score) return '#94a3b8';
        if (score >= 701) return '#10b981'; // Muito Baixo Risco — verde
        if (score >= 501) return '#0ea5e9'; // Baixo Risco — azul
        if (score >= 301) return '#f59e0b'; // Risco Médio — âmbar
        return '#ef4444';                   // Alto Risco — vermelho
    };

    const getScoreLabel = (score?: number) => {
        if (!score) return 'Não informado';
        if (score >= 701) return 'Muito Baixo Risco';
        if (score >= 501) return 'Baixo Risco';
        if (score >= 301) return 'Risco Médio';
        return 'Alto Risco';
    };

    const inputBase = `w-full h-10 px-3.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/60 text-sm font-medium text-slate-800 dark:text-slate-200 focus:outline-none transition-all duration-200 placeholder:text-slate-400 shadow-sm cursor-default`;
    const labelBase = "text-[11px] font-extrabold text-slate-700 dark:text-slate-300 uppercase tracking-[0.08em] mb-0.5";
    const sectionCard = "bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden";
    const readOnlyInput = inputBase + ' bg-slate-50 dark:bg-slate-800/60 cursor-default';

    const hasDocumento = !!negociacao?.cpf_cnpj;
    const documentoDisplay = negociacao?.cpf_cnpj ? formatDocumento(negociacao.cpf_cnpj) : '—';
    const tipoPessoaDisplay = isPJ ? 'CNPJ' : 'CPF';

    // Format data_fundacao for display (non-API dates — no offset correction)
    const formatDataFundacao = (dateStr?: string) => {
        if (!dateStr) return '—';
        try {
            if (dateStr.includes('/')) return dateStr;
            if (dateStr.includes('-')) {
                const parts = dateStr.split('-');
                if (parts.length === 3) {
                    // Parse as local time to avoid UTC offset shifting
                    const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                    return d.toLocaleDateString('pt-BR');
                }
            }
            return dateStr;
        } catch {
            return dateStr;
        }
    };

    /**
     * Format Serasa API dates. Parses ISO dates as local time to avoid UTC offset shifting.
     * The date stored in the DB is already the correct display date (no +1 needed).
     */
    const formatSerasaApiDate = (dateStr?: string): string => {
        if (!dateStr) return '—';
        try {
            const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
                const d = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
                return d.toLocaleDateString('pt-BR');
            }
            if (dateStr.includes('/')) return dateStr;
            return dateStr;
        } catch {
            return dateStr;
        }
    };

    /**
     * Calculate years since a Serasa API date (local-time parsing, no UTC offset).
     */
    const calcularAnosSerasaApiDate = (dateStr?: string): number | null => {
        if (!dateStr) return null;
        try {
            const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (!match) return null;
            const d = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
            if (isNaN(d.getTime())) return null;
            const today = new Date();
            let age = today.getFullYear() - d.getFullYear();
            const m = today.getMonth() - d.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
            return age >= 0 && age < 200 ? age : null;
        } catch {
            return null;
        }
    };

    /** Calcula idade em anos a partir de uma data (ISO, dd/mm/yyyy ou yyyy-mm-dd) */
    const calcularIdade = (dateStr?: string): number | null => {
        if (!dateStr) return null;
        try {
            let d: Date;
            if (dateStr.includes('/')) {
                const parts = dateStr.split('/');
                if (parts.length === 3) d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                else return null;
            } else {
                d = new Date(dateStr);
            }
            if (isNaN(d.getTime())) return null;
            const today = new Date();
            let age = today.getFullYear() - d.getFullYear();
            const m = today.getMonth() - d.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
            return age >= 0 && age < 150 ? age : null;
        } catch {
            return null;
        }
    };

    return (
        <div className="space-y-6 animate-slide-up">
            {/* ── Header Card: Inteligência Serasa ── */}
            <div className={sectionCard}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 gap-3">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-100 dark:border-emerald-800/30">
                            <CreditCard size={18} className="text-emerald-700 dark:text-emerald-400" />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">Relatório Básico Serasa</h3>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                {isPJ ? 'Pessoa Jurídica' : 'Pessoa Física'} • Relatório anexado manualmente
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Status da consulta — linguagem leiga */}
                        {hasExistingData && consultaTime && (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold border bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/40">
                                <CheckCircle2 size={12} />
                                <span>
                                    Consultado{consultaUserName ? ` por ${consultaUserName}` : ''} {timeAgo(consultaTime)}
                                </span>
                            </div>
                        )}

                        {/* Anexo do relatório Serasa (PDF/imagem/ZIP) */}
                        <div
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDraggingHeader(true); }}
                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDraggingHeader(false); }}
                            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDraggingHeader(false); const f = e.dataTransfer.files?.[0]; if (f) handleSerasaUpload(f); }}
                        >
                            <input ref={serasaInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.zip" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSerasaUpload(f); e.target.value = ''; }} />
                            {data.credito_relatorio_serasa_url ? (
                                <div className="flex items-center gap-2">
                                    <a href={data.credito_relatorio_serasa_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50 rounded-xl font-bold text-xs hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-all shadow-sm hover:-translate-y-[1px]">
                                        <FileText size={14} /> {/\.zip($|\?)/i.test(data.credito_relatorio_serasa_url) ? 'ZIP Anexado' : 'PDF Anexado'} <ExternalLink size={11} />
                                    </a>
                                    <button onClick={handleRemoveSerasaFile} className="p-2 text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all" title="Remover anexo">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => serasaInputRef.current?.click()}
                                    disabled={uploadingSerasa}
                                    className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl font-bold text-xs transition-all shadow-sm disabled:opacity-50 hover:-translate-y-[1px] ${
                                        draggingHeader
                                            ? 'bg-primary/10 border-primary text-primary scale-[1.03]'
                                            : 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800/50 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                                    }`}
                                    title="Anexar relatório de crédito — PDF, imagem ou ZIP (use ZIP para análise coletiva, com um PDF por associado)"
                                >
                                    {uploadingSerasa ? <><Loader2 size={14} className="animate-spin" /> Enviando...</> : <><Upload size={14} /> {draggingHeader ? 'Solte aqui' : 'Anexar PDF/ZIP'}</>}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Document info bar */}
                {hasDocumento && (
                    <div className="px-6 py-2 bg-slate-50/80 dark:bg-slate-800/30 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Documento:</span>
                        <span className="text-sm font-bold text-slate-700 dark:text-slate-200 font-mono tracking-wide">{documentoDisplay}</span>
                        <span className={`px-2 py-0.5 text-[9px] font-bold rounded-full ${
                            isPJ
                                ? 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                                : 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                        }`}>
                            {isPJ ? 'Pessoa Jurídica' : 'Pessoa Física'}
                        </span>
                        {negociacao?.cliente_nome && (
                            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium ml-auto truncate max-w-[250px]">{negociacao.cliente_nome}</span>
                        )}
                    </div>
                )}

                {!hasExistingData && (
                    <div className="mx-6 mt-4 flex items-start gap-2.5 p-3 rounded-xl bg-sky-50 dark:bg-sky-900/15 border border-sky-200 dark:border-sky-800/40">
                        <Info size={14} className="text-sky-600 dark:text-sky-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-sky-800 dark:text-sky-300">
                            <strong>A consulta automática ao Serasa foi descontinuada.</strong> Os campos abaixo ficam vazios
                            em análises novas — anexe o relatório de crédito em PDF/ZIP no botão acima e registre as
                            conclusões no parecer. Análises anteriores a 28/08/2026 seguem exibindo os dados já consultados.
                        </p>
                    </div>
                )}

                <div className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                        {/* Score Display — fixed height to align with data grid */}
                        <div className={`flex flex-col items-center justify-between bg-gradient-to-b from-slate-50 to-white dark:from-slate-800/40 dark:to-slate-900/40 border border-slate-100 dark:border-slate-800 rounded-2xl p-5 min-w-[200px] transition-all duration-500`}>
                            <span className={labelBase + " mb-2"}>{isPJ ? 'SCORE EMPRESAS' : 'SCORE DE CRÉDITO'}</span>
                            <div className="relative w-24 h-24 flex items-center justify-center mb-2">
                                <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                                    <circle cx="60" cy="60" r="50" fill="none" stroke="#e2e8f0" strokeWidth="7" className="dark:stroke-slate-700" />
                                    <circle
                                        cx="60" cy="60" r="50" fill="none"
                                        stroke={getScoreColor(serasa.score)}
                                        strokeWidth="7"
                                        strokeLinecap="round"
                                        strokeDasharray={gaugeAnimated ? `${((serasa.score || 0) / (isPJ ? 500 : 1000)) * 314} 314` : '0 314'}
                                        className="transition-all duration-1000 ease-out"
                                        style={{ filter: `drop-shadow(0 0 4px ${getScoreColor(serasa.score)}40)` }}
                                    />
                                </svg>
                                {/* Centro do gauge: score + badge Risco de Crédito (da API) */}
                                <div className="absolute flex flex-col items-center gap-1">
                                    <span className="text-2xl font-black text-slate-800 dark:text-slate-200 tabular-nums">{serasa.score || '—'}</span>
                                    {(() => {
                                        // Usa risco_credito salvo pela API; fallback pela escala oficial Serasa
                                        let label: string | null = serasa.risco_credito || null;
                                        if (!label && serasa.score) {
                                            label = getScoreLabel(serasa.score);
                                        }
                                        if (!label) return null;
                                        const colorClass = label === 'Muito Baixo Risco'
                                            ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                                            : label === 'Baixo Risco'
                                            ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400'
                                            : label === 'Risco Médio'
                                            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                                            : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
                                        return (
                                            <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wide ${colorClass}`}>
                                                {label}
                                            </span>
                                        );
                                    })()}
                                </div>
                            </div>
                            <input
                                type="number" min="0" max="1000"
                                value={serasa.score ?? ''}
                                readOnly
                                className="w-24 h-8 px-2 text-center border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/60 text-base font-black text-slate-700 dark:text-slate-200 cursor-default shadow-sm"
                                placeholder="—"
                            />
                            {/* Chance de Pagamento (PF) / Prob. Inadimplência (PJ) */}
                            <div className="mt-3 w-full max-w-[170px]">
                                <label className="text-[9px] font-extrabold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1 block text-center">
                                    {isPJ ? 'Prob. Inadimplência' : 'Chance de Pagamento'}
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={chancePagamentoText}
                                        readOnly
                                        className="w-full h-8 px-3 pr-7 text-center border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/60 text-sm font-bold text-slate-700 dark:text-slate-200 cursor-default shadow-sm"
                                        placeholder="—"
                                    />
                                    <Percent size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                </div>
                            </div>
                        </div>

                        {/* Data Grid — Anotações Negativas */}
                        <div className="flex-1 flex flex-col">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <FieldPair label="Dívidas com Empresas (PEFIN)" qtdField="dividas_comerciais" valField="valor_dividas_comerciais" serasa={serasa} updateSerasa={updateSerasa} inputBase={inputBase} labelBase={labelBase} flash={false} />
                                <FieldPair label="Dívidas com Bancos (REFIN)" qtdField="dividas_bancarias" valField="valor_dividas_bancarias" serasa={serasa} updateSerasa={updateSerasa} inputBase={inputBase} labelBase={labelBase} flash={false} />
                                <FieldPair label={isPJ ? 'Dívidas Vencidas' : 'Dívidas Vencidas (Convem)'} qtdField="dividas_vencidas" valField="valor_dividas_vencidas" serasa={serasa} updateSerasa={updateSerasa} inputBase={inputBase} labelBase={labelBase} flash={false} />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                <FieldPair label="Protestos Nacional" qtdField="quantidade_protestos" valField="valor_total_protestos" serasa={serasa} updateSerasa={updateSerasa} inputBase={inputBase} labelBase={labelBase} flash={false} />
                                <FieldPair label="Cheques Devolvidos" qtdField="quantidade_cheques_sf" valField="valor_total_cheques_sf" serasa={serasa} updateSerasa={updateSerasa} inputBase={inputBase} labelBase={labelBase} flash={false} />

                            </div>

                            {/* Total de dívidas — alinhado na mesma altura do score card */}
                            <div className="mt-4 flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200/60 dark:border-slate-700/50">
                                <span className="text-[11px] font-extrabold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Total de Dívidas</span>
                                <div className="flex items-center gap-4">
                                    {(() => {
                                        const totalQtd = (serasa.dividas_comerciais || 0) + (serasa.dividas_bancarias || 0) + (serasa.dividas_vencidas || 0) + (serasa.quantidade_protestos || 0) + (serasa.quantidade_cheques_sf || 0);
                                        const totalVal = (serasa.valor_dividas_comerciais || 0) + (serasa.valor_dividas_bancarias || 0) + (serasa.valor_dividas_vencidas || 0) + (serasa.valor_total_protestos || 0) + (serasa.valor_total_cheques_sf || 0);
                                        return (
                                            <>
                                                <span className="text-sm font-black text-slate-700 dark:text-slate-200 tabular-nums">{totalQtd} registro{totalQtd !== 1 ? 's' : ''}</span>
                                                <span className={`text-sm font-black tabular-nums ${totalVal > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{fmtBRL(totalVal)}</span>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>


                        </div>
                    </div>
                </div>
            </div>

            {/* ── PF: Identificação Cadastral ── */}
            {!isPJ && (
                <div className={sectionCard} style={{ animationDelay: '80ms' }}>
                    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                        <div className="p-1.5 bg-blue-50 dark:bg-blue-900/15 rounded-lg border border-blue-100 dark:border-blue-800/30">
                            <User size={14} className="text-blue-700 dark:text-blue-400" />
                        </div>
                        <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Identificação Cadastral</h4>
                    </div>
                    <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Situação RF + data de atualização */}
                        <div className="space-y-1.5">
                            <label className={labelBase}>Situação Receita Fed.</label>
                            <input
                                type="text"
                                readOnly
                                value={(() => {
                                    let raw = serasa.situacao_receita_federal || '';
                                    const colonIdx = raw.lastIndexOf(':');
                                    if (colonIdx > -1) raw = raw.substring(colonIdx + 1).trim();
                                    const v = raw.toLowerCase();
                                    const map: Record<string, string> = { regular: 'Regular', pendente: 'Pendente', suspensa: 'Suspensa', cancelada: 'Cancelada', nula: 'Nula', inapta: 'Inapta', ativa: 'Ativa' };
                                    return map[v] || serasa.situacao_receita_federal || '—';
                                })()}
                                className={readOnlyInput}
                            />
                            {serasa.data_situacao_rf && (
                                <p className="text-[10px] text-slate-400 font-medium pl-1">
                                    Atualizado em {formatDataFundacao(serasa.data_situacao_rf)}
                                </p>
                            )}
                        </div>

                        {/* Data de Nascimento + Idade */}
                        <div className="space-y-1.5">
                            <label className={labelBase}>Data de Nascimento</label>
                            <div className="relative">
                                {(() => {
                                    // Serasa API date needs +1 day correction; fallback date does not
                                    const serasaDate = serasa.data_nascimento;
                                    const fallbackDate = negociacao?.data_nascimento_cliente;
                                    const displayDate = serasaDate
                                        ? formatSerasaApiDate(serasaDate)
                                        : fallbackDate
                                        ? formatDataFundacao(fallbackDate)
                                        : '—';
                                    const idade = serasaDate
                                        ? calcularAnosSerasaApiDate(serasaDate)
                                        : calcularIdade(fallbackDate);
                                    return (
                                        <>
                                            <input type="text" value={displayDate} readOnly className={readOnlyInput} placeholder="—" />
                                            <CalendarIcon size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                            {idade !== null && (
                                                <p className="text-[10px] text-slate-400 font-medium pl-1">{idade} anos</p>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Nome da Mãe */}
                        <div className="space-y-1.5">
                            <label className={labelBase}>Nome da Mãe</label>
                            <input type="text" value={serasa.nome_mae || '—'} readOnly className={readOnlyInput} placeholder="—" />
                        </div>

                        {/* Município/UF */}
                        <div className="space-y-1.5">
                            <label className={labelBase}>Município/UF</label>
                            <input
                                type="text"
                                readOnly
                                value={(() => {
                                    if (serasa.cidade_pf && serasa.uf_pf) return `${serasa.cidade_pf}/${serasa.uf_pf}`;
                                    if (serasa.cidade_pf) return serasa.cidade_pf;
                                    if (serasa.uf_pf) return serasa.uf_pf;
                                    return '—';
                                })()}
                                className={readOnlyInput}
                                placeholder="—"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ── PJ: Identificação e Endereço ── */}
            {isPJ && (
                <div className={sectionCard} style={{ animationDelay: '80ms' }}>
                    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                        <div className="p-1.5 bg-emerald-50 dark:bg-emerald-900/15 rounded-lg border border-emerald-100 dark:border-emerald-800/30">
                            <Building2 size={14} className="text-emerald-700 dark:text-emerald-400" />
                        </div>
                        <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Identificação e Endereço</h4>
                    </div>
                    <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelBase}>Situação Receita Fed.</label>
                            <input
                                type="text"
                                readOnly
                                value={(() => {
                                    let raw = serasa.situacao_receita_federal || '';
                                    const colonIdx = raw.lastIndexOf(':');
                                    if (colonIdx > -1) raw = raw.substring(colonIdx + 1).trim();
                                    const v = raw.toLowerCase();
                                    const map: Record<string, string> = { regular: 'Regular', pendente: 'Pendente', suspensa: 'Suspensa', cancelada: 'Cancelada', nula: 'Nula', inapta: 'Inapta', ativa: 'Ativa' };
                                    return map[v] || serasa.situacao_receita_federal || '—';
                                })()}
                                className={readOnlyInput}
                            />
                        </div>
                        <div className="space-y-1.5 md:col-span-2">
                            <label className={labelBase}>Endereço Sede</label>
                            <input type="text" value={serasa.endereco_sede || ''} readOnly className={readOnlyInput} placeholder="—" />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelBase}>Município/UF</label>
                            <input type="text" value={serasa.municipio_uf || ''} readOnly className={readOnlyInput} placeholder="—" />
                        </div>
                        <div className="space-y-1.5 md:col-span-2">
                            <label className={labelBase}>Data de Fundação</label>
                            <div className="relative">
                                <input type="text" value={formatSerasaApiDate(serasa.data_fundacao)} readOnly className={readOnlyInput} placeholder="—" />
                                <CalendarIcon size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                            </div>
                            {serasa.data_fundacao && (() => {
                                const anos = calcularAnosSerasaApiDate(serasa.data_fundacao);
                                return anos !== null ? (
                                    <p className="text-[10px] text-slate-400 font-medium pl-1">
                                        {anos} ano{anos !== 1 ? 's' : ''}
                                    </p>
                                ) : null;
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* ── PF: Participações Societárias ── */}
            {!isPJ && (
                <div className={sectionCard} style={{ animationDelay: '100ms' }}>
                    <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-2.5">
                            <div className="p-1.5 bg-primary/8 dark:bg-primary/15 rounded-lg">
                                <Building2 size={14} className="text-primary" />
                            </div>
                            <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Participações Societárias</h4>
                            {(serasa.participacoes_societarias?.length || 0) > 0 && (
                                <span className="px-2 py-0.5 text-[10px] font-bold bg-primary/10 text-primary rounded-full">{serasa.participacoes_societarias!.length}</span>
                            )}
                        </div>
                    </div>
                    <div className="p-4">
                        {!serasa.participacoes_societarias || serasa.participacoes_societarias.length === 0 ? (
                            <div className="flex flex-col items-center py-5 gap-2">
                                <Building2 size={20} className="text-slate-300 dark:text-slate-600" />
                                <p className="text-xs text-slate-400 font-medium">Nenhuma participação registrada</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-100 dark:border-slate-700/50">
                                            <th className="text-left px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase tracking-wider">CNPJ</th>
                                            <th className="text-left px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase tracking-wider">Razão Social</th>
                                            <th className="text-center px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase tracking-wider">Capital %</th>
                                            <th className="text-center px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase tracking-wider">UF</th>
                                            <th className="text-center px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase tracking-wider">Situação RF</th>
                                            <th className="text-center px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase tracking-wider">Data Situação</th>
                                            <th className="text-center px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase tracking-wider">Atualizado em</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                        {serasa.participacoes_societarias.map((p, i) => (
                                            <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                                                <td className="px-3 py-2.5 font-mono font-medium text-slate-600 dark:text-slate-300">{p.cnpj}</td>
                                                <td className="px-3 py-2.5 font-bold text-slate-700 dark:text-slate-200">{p.razao_social}</td>
                                                <td className="text-center px-3 py-2.5 font-bold text-primary tabular-nums">{p.capital_percentual}</td>
                                                <td className="text-center px-3 py-2.5 font-medium text-slate-500">{p.uf}</td>
                                                <td className="text-center px-3 py-2.5">
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                        p.situacao_rf?.toLowerCase() === 'ativa' || p.situacao_rf?.toLowerCase() === 'regular'
                                                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                                                            : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                                                    }`}>{p.situacao_rf || '—'}</span>
                                                </td>
                                                <td className="text-center px-3 py-2.5 text-slate-500 tabular-nums">
                                                    {p.data_situacao ? formatDataFundacao(p.data_situacao) : '—'}
                                                </td>
                                                <td className="text-center px-3 py-2.5 text-slate-500 tabular-nums">
                                                    {p.atualizado_em ? formatDataFundacao(p.atualizado_em) : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── PF: Documentos Roubados ── */}
            {!isPJ && (
                <div className="flex items-center gap-3 px-5 py-3 bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm" style={{ animationDelay: '120ms' }}>
                    <div className="p-1.5 bg-amber-50 dark:bg-amber-900/15 rounded-lg">
                        <FileWarning size={14} className="text-amber-600 dark:text-amber-400" />
                    </div>
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Documentos Roubados</span>
                    <span className={`ml-auto px-3 py-1 rounded-full text-[11px] font-bold ${
                        serasa.documentos_roubados
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                    }`}>
                        {serasa.documentos_roubados ? 'SIM — Ocorrências encontradas' : 'Sem ocorrências'}
                    </span>
                </div>
            )}

            {/* ── PJ: Quadro Societário ── */}
            {isPJ && (
                <div className={sectionCard} style={{ animationDelay: '100ms' }}>
                    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                        <div className="p-1.5 bg-primary/8 dark:bg-primary/15 rounded-lg">
                            <Users size={14} className="text-primary" />
                        </div>
                        <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Quadro Societário e Administrativo</h4>
                    </div>
                    <div className="p-4 space-y-4">
                        {/* Sócios */}
                        <div>
                            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2">Sócios e Acionistas</p>
                            {!serasa.quadro_societario?.socios || serasa.quadro_societario.socios.length === 0 ? (
                                <p className="text-xs text-slate-400 italic py-2">Nenhum sócio registrado</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="border-b border-slate-100 dark:border-slate-700/50">
                                                <th className="text-left px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase">Nome</th>
                                                <th className="text-left px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase">CPF/CNPJ</th>
                                                <th className="text-center px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase">Capital %</th>
                                                <th className="text-center px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase">Anotações</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                            {serasa.quadro_societario.socios.map((s, i) => (
                                                <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                                                    <td className="px-3 py-2.5 font-bold text-slate-700 dark:text-slate-200">{s.nome}</td>
                                                    <td className="px-3 py-2.5 font-mono text-slate-500">{s.cpf_cnpj}</td>
                                                    <td className="text-center px-3 py-2.5 font-bold text-primary tabular-nums">{s.capital_percentual}</td>
                                                    <td className="text-center px-3 py-2.5">
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${s.anotacoes ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                                            {s.anotacoes ? 'Sim' : 'Não'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        {/* Administradores */}
                        <div>
                            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2">Administradores</p>
                            {!serasa.quadro_societario?.administradores || serasa.quadro_societario.administradores.length === 0 ? (
                                <p className="text-xs text-slate-400 italic py-2">Nenhum administrador registrado</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="border-b border-slate-100 dark:border-slate-700/50">
                                                <th className="text-left px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase">Nome</th>
                                                <th className="text-left px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase">Cargo</th>
                                                <th className="text-left px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase">CPF/CNPJ</th>
                                                <th className="text-center px-3 py-2 font-extrabold text-[10px] text-slate-400 uppercase">Anotações</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                            {serasa.quadro_societario.administradores.map((a, i) => (
                                                <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                                                    <td className="px-3 py-2.5 font-bold text-slate-700 dark:text-slate-200">{a.nome}</td>
                                                    <td className="px-3 py-2.5 text-slate-500">{a.cargo}</td>
                                                    <td className="px-3 py-2.5 font-mono text-slate-500">{a.cpf_cnpj}</td>
                                                    <td className="text-center px-3 py-2.5">
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${a.anotacoes ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                                            {a.anotacoes ? 'Sim' : 'Não'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}


            {/* ── Parecer Consolidado ── */}
            <div className={sectionCard}>
                <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                    <div className="p-1.5 bg-primary/8 dark:bg-primary/15 rounded-lg">
                        <CreditCard size={14} className="text-primary" />
                    </div>
                    <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Parecer Consolidado</h4>
                </div>
                <div className="p-5">
                    <textarea
                        name="parecer_credito"
                        value={data.parecer_credito || ''}
                        onChange={handleChange}
                        rows={4}
                        className="w-full rounded-xl border border-slate-200 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/40 px-4 py-3 text-sm text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 resize-none shadow-sm transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 font-medium hover:border-slate-300 dark:hover:border-slate-600"
                        placeholder="Digite aqui o seu raciocínio final e considerações da análise de crédito..."
                    />
                </div>
            </div>

        </div>
    );
}

/** Reusable field pair component (Qtd + Valor) */
function FieldPair({ label, qtdField, valField, serasa, updateSerasa, inputBase, labelBase, flash }: {
    label: string;
    qtdField: keyof SerasaExperianData;
    valField: keyof SerasaExperianData;
    serasa: SerasaExperianData;
    updateSerasa: (field: keyof SerasaExperianData, value: any) => void;
    inputBase: string;
    labelBase: string;
    flash: boolean;
}) {
    return (
        <div className="space-y-1.5">
            <label className={labelBase}>{label}</label>
            <div className="flex gap-2">
                <div className="flex-1">
                    <span className="text-[9px] font-bold text-slate-400 uppercase">Qtd</span>
                    <input
                        type="number"
                        value={(serasa[qtdField] as number) ?? ''}
                        readOnly
                        className={inputBase + ' bg-slate-50 dark:bg-slate-800/60 cursor-default'}
                        placeholder="—"
                    />
                </div>
                <div className="flex-1">
                    <span className="text-[9px] font-bold text-slate-400 uppercase">R$ Valor</span>
                    <input
                        type="number"
                        value={(serasa[valField] as number) ?? ''}
                        readOnly
                        className={inputBase + ' bg-slate-50 dark:bg-slate-800/60 cursor-default'}
                        placeholder="—"
                    />
                </div>
            </div>
        </div>
    );
}
